import { useLogoutMutation } from '@codezo/graphql';
import { Center, Loader, Stack, Text } from '@mantine/core';
import { useEffect } from 'react';
import { clearRefreshToken, getStoredRefreshToken } from '../utils';

interface LogoutCallbackProps {
	setUser: (_user: null) => void;
	setAccessToken: (_token: null) => void;
	token: string | null;
}

export function LogoutCallback({ setUser, setAccessToken, token }: LogoutCallbackProps) {
	const [logoutMutation] = useLogoutMutation();

	useEffect(() => {
		let isLoggedOut = false;

		const performLogout = async () => {
			if (isLoggedOut) return; // Prevent double execution
			isLoggedOut = true;

			try {
				const refreshToken = getStoredRefreshToken();
				const currentToken = token;

				// Call logout mutation if we have tokens
				if (currentToken && refreshToken) {
					await logoutMutation({
						variables: {
							input: {
								token: currentToken,
								refreshToken,
							},
						},
					});
				}
			} catch {
				// Continue with logout even if mutation fails
				// Removed console.warn
			}

			// Clear local state
			setUser(null);
			setAccessToken(null);
			clearRefreshToken();

			// Redirect to public app after a brief delay
			setTimeout(() => {
				window.location.replace(import.meta.env.VITE_PUBLIC_APP_URL || '/');
			}, 1500);
		};

		performLogout();
	}, [logoutMutation, setAccessToken, setUser, token]);

	return (
		<Center h="100vh">
			<Stack align="center" gap="lg">
				<Loader size="lg" color="codezo" />
				<Stack align="center" gap="xs">
					<Text size="lg" fw={500}>
						Logging out...
					</Text>
					<Text size="sm" c="dimmed">
						You are being securely logged out.
					</Text>
				</Stack>
			</Stack>
		</Center>
	);
}
